import React, { useState, useEffect, useRef } from 'react'
import {
  Button,
  Icon,
  message,
  Upload,
  Tooltip,
  Spin,
  Card,
  Progress,
  Alert,
  Avatar,
  Modal,
  Radio
} from 'antd'
import {
  listAIVoices,
  uploadCustomVoice,
  getTaskStatus
} from '../../../../services/api/aiVideoGeneration'
import StepTitle from '../StepTitle'
import VoiceSelectionModal from './VoiceSelectionModal'
import { useVoiceoverHandler } from './useVoiceoverHandler'
import AudioModeSelectionModal from './AudioModeSelectionModal'
import AudioModeToggle from './AudioModeToggle'
import AudioModeChangeModal from './AudioModeChangeModal'
import './Voiceover.scss'
import { config } from '../../../../constants'
import { clearSceneCurrentAudio } from '../../utils/audioHistory'

import { getToken } from '../../../../services/api/user'

const AUDIO_PREVIEW_BASE_URL = `${config.videoGenerationUrl.replace(
  /\/?$/,
  '/'
)}audio/`

const Voiceover = ({
  formData,
  setFormData,
  onValidationChange,
  voices,
  setVoices,
  onVoiceChange,
  isClusteringAudio,
  audioClusteringStatus,
  audioClusteringProgress,
  isDraftMode = false,
  videoId,
  isShotsReady = true,
  shotGenerationStyle = null,
  shotsGenerationProgress = 0
}) => {
  const [loadingVoices, setLoadingVoices] = useState(false)
  const [playingVoice, setPlayingVoice] = useState(null)
  const [showUpload, setShowUpload] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState(null)
  const [pendingUploadFile, setPendingUploadFile] = useState(null)
  const [pendingVoiceName, setPendingVoiceName] = useState('')
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingVoiceId, setPendingVoiceId] = useState(null)
  const [activeTarget, setActiveTarget] = useState('narrator') // 'narrator' or characterKey
  const [isSelectionModalVisible, setIsSelectionModalVisible] = useState(false)
  const audioRef = useRef(null)

  const [audioProgress, setAudioProgress] = useState(0)

  // Custom handler for audio mode
  const {
    handleModeChange,
    handleConfirmMode,
    audioMode,
    showModeModal,
    showChangeModal,
    pendingMode,
    targetHasAudio,
    handleModeChangeCancel,
    handleModeChangeContinue,
    handleModeChangeRegenerate
  } = useVoiceoverHandler({
    formData,
    setFormData
  })

  // Get selected voice from formData
  const selectedVoice = formData?.selectedVoice || null

  useEffect(() => {
    if (voices.length === 0) {
      fetchVoices()
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  // Clear error when closing upload panel
  useEffect(() => {
    if (!showUpload) {
      setUploadError(null)
    }
  }, [showUpload])

  // Validate step when voice is selected
  const onValidationChangeRef = useRef(onValidationChange)

  useEffect(() => {
    onValidationChangeRef.current = onValidationChange
  }, [onValidationChange])

  // Validate step when voice is selected and mode is confirmed
  useEffect(() => {
    if (onValidationChangeRef.current) {
      // Step is valid if a voice mode exists
      const isValid = !!formData?.audioMode
      onValidationChangeRef.current(isValid)
    }
  }, [formData?.audioMode])

  const waitForTaskSuccess = async taskId => {
    if (!taskId) throw new Error('Missing task id')

    const maxAttempts = 100
    const delayMs = 3000

    await new Promise(resolve => setTimeout(resolve, 2000))

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const statusResponse = await getTaskStatus(taskId)
        const data = statusResponse?.data || null
        const status = data?.status || null

        if (status === 'success') {
          const output = data?.output || data?.audio_url || data?.result || null
          if (output?.success === false || output?.error) {
            const errorMsg = output?.error || 'Operation failed'
            throw new Error(errorMsg)
          }
          return output
        }

        if (status === 'failed' || status === 'error') {
          const errorMsg = data?.output?.error || data?.error || 'Task failed'
          throw new Error(errorMsg)
        }
      } catch (err) {
        if (err.message === 'Task failed' || attempt === maxAttempts - 1)
          throw err
      }

      await new Promise(resolve => setTimeout(resolve, delayMs))
    }

    throw new Error('Task polling timed out')
  }

  const fetchVoices = async () => {
    setLoadingVoices(true)
    try {
      const res = await listAIVoices(videoId)
      const task_id = res?.data?.task_id

      if (!task_id) {
        console.error('No task_id received from listAIVoices')
        setVoices([])
        return
      }

      const data = await waitForTaskSuccess(task_id)

      if (!data) {
        console.error('No data received from task')
        setVoices([])
        return
      }

      const defaultVoices = data?.default_voices || []
      const uploadedVoices = data?.uploaded_voices || []

      const allVoices = [
        ...defaultVoices.map((voice, index) => ({
          id: voice,
          name: `Voice ${index + 1}`,
          type: 'default'
        })),
        ...uploadedVoices.map(voice => ({
          id: voice,
          name: voice,
          type: 'uploaded'
        }))
      ]

      setVoices(allVoices)
    } catch (err) {
      console.error('Failed to fetch voices', err)
      message.error('Failed to load voices')
      setVoices([])
    } finally {
      setLoadingVoices(false)
    }
  }

  const handleSelectVoice = voiceId => {
    if (isClusteringAudio) {
      Modal.info({
        title: 'Audio Generation in Progress',
        content:
          'Audio generation is currently in progress for your scenes. Please wait for it to complete before changing the voice.',
        centered: true
      })
      return
    }

    const hasAnyAudioLink = formData?.shotList?.some(
      scene => !!scene.audio_link
    )

    if (!isDraftMode && hasAnyAudioLink) {
      setPendingVoiceId(voiceId)
      setShowConfirmationModal(true)
    } else {
      applyVoiceSelection(voiceId)
      setIsSelectionModalVisible(false)
    }
  }

  const handleClone = () => {
    Modal.confirm({
      title: 'Confirm Clone',
      content:
        'Are you sure you want to create a new copy of this video with the new voice?',
      centered: true,
      onOk: () => {
        if (onVoiceChange && pendingVoiceId) {
          onVoiceChange(
            pendingVoiceId,
            true,
            activeTarget === 'narrator' ? null : activeTarget
          ) // true = clone
        }
        setShowConfirmationModal(false)
        setPendingVoiceId(null)
      }
    })
  }

  const handleOverride = () => {
    Modal.confirm({
      title: 'Confirm Override',
      content:
        'This will regenerate audio for all scenes. Are you sure you want to continue?',
      centered: true,
      okType: 'danger',
      onOk: () => {
        if (pendingVoiceId) {
          applyVoiceSelection(pendingVoiceId)
          setIsSelectionModalVisible(false) // Close modal on selection
        }
        setShowConfirmationModal(false)
        setPendingVoiceId(null)
      }
    })
  }

  const handleCancelChange = () => {
    setShowConfirmationModal(false)
    setPendingVoiceId(null)
  }

  const getPendingVoiceName = () => {
    const voice = voices.find(v => v.id === pendingVoiceId)
    return voice?.name || pendingVoiceId
  }

  const applyVoiceSelection = voiceId => {
    if (onVoiceChange) {
      onVoiceChange(
        voiceId,
        false,
        activeTarget === 'narrator' ? null : activeTarget
      )
    } else {
      setFormData(prev => {
        const updatedData = {
          ...prev,
          voiceoverAudio: null,
          shotList: (prev.shotList || []).map(scene => ({
            ...clearSceneCurrentAudio(scene),
            shots: scene.shots.map(shot => {
              const { audio_link, ...restShot } = shot
              return {
                ...restShot,
                audio_duration: null
              }
            })
          }))
        }

        if (activeTarget === 'narrator') {
          updatedData.selectedVoice = voiceId
        } else {
          updatedData.characterVoices = {
            ...(prev.characterVoices || {}),
            [activeTarget]: voiceId
          }
        }

        return updatedData
      })
    }
  }

  const handlePlayPreview = voiceId => {
    if (playingVoice === voiceId) {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      setPlayingVoice(null)
      setAudioProgress(0)
    } else {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.ontimeupdate = null
      }
      setAudioProgress(0)
      const audio = new Audio(`${AUDIO_PREVIEW_BASE_URL}${voiceId}`)

      console.log({
        audiourl: `${AUDIO_PREVIEW_BASE_URL}${voiceId}`
      })

      audio.ontimeupdate = () => {
        if (audio.duration) {
          const progress = (audio.currentTime / audio.duration) * 100
          setAudioProgress(progress)
        }
      }

      audio.play().catch(err => {
        console.error('Audio preview failed:', err)
        message.error('Failed to play audio preview')
      })

      audio.onended = () => {
        setPlayingVoice(null)
        setAudioProgress(0)
      }

      audioRef.current = audio
      setPlayingVoice(voiceId)
      setAudioProgress(0)
    }
  }

  const handleUploadVoice = async options => {
    const { file } = options
    const derivedName = file?.name?.replace(/\.[^/.]+$/, '') || 'new_voice'
    setPendingUploadFile(file)
    setPendingVoiceName(derivedName)
    await uploadVoiceFile(file, derivedName, false, options)
  }

  const uploadVoiceFile = async (
    file,
    voiceName,
    overwrite = false,
    options = {}
  ) => {
    const { onSuccess, onError } = options
    if (!file) return

    const uploadFormData = new FormData()
    uploadFormData.append('voice_file', file)
    uploadFormData.append('voice_name', voiceName)
    if (overwrite) {
      uploadFormData.append('overwrite', 'true')
    }

    setUploading(true)
    setUploadProgress(0)
    setUploadError(null)

    let progressInterval = null
    try {
      const token = await getToken()
      progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90))
      }, 200)

      await uploadCustomVoice(uploadFormData, token)
      setUploadProgress(100)

      message.success('Voice uploaded successfully!')
      setShowUpload(false)
      setPendingUploadFile(null)
      setPendingVoiceName('')

      await fetchVoices()
      if (onSuccess) onSuccess()
    } catch (err) {
      console.error('Upload failed:', err)
      const detail = err?.response?.data?.detail
      const apiError = err?.response?.data?.error
      let errorMsg = 'Failed to upload voice'

      if (err.response?.status === 413) {
        errorMsg = 'File is too large. Please upload a file smaller than 50MB.'
      } else if (
        typeof detail === 'string' &&
        detail.includes('already exists')
      ) {
        errorMsg = `${detail} You can edit the voice name and retry, or overwrite existing.`
      } else if (apiError) {
        errorMsg = apiError
      } else if (detail) {
        errorMsg = detail
      } else if (err.message) {
        errorMsg = err.message
      }

      setUploadError(errorMsg)
      if (onError) onError(err)
    } finally {
      if (progressInterval) clearInterval(progressInterval)
      setUploading(false)
      setUploadProgress(0)
    }
  }

  // Pre-upload validation
  const beforeUpload = file => {
    setUploadError(null)
    const isLt50M = file.size / 1024 / 1024 < 50
    if (!isLt50M) {
      setUploadError('File must be smaller than 50MB!')
      return false
    }
    return true
  }

  const handleRetryUpload = async () => {
    if (!pendingUploadFile || !pendingVoiceName?.trim()) return
    await uploadVoiceFile(pendingUploadFile, pendingVoiceName.trim(), false)
  }

  const handleOverwriteUpload = async () => {
    if (!pendingUploadFile || !pendingVoiceName?.trim()) return
    await uploadVoiceFile(pendingUploadFile, pendingVoiceName.trim(), true)
  }

  const getSelectedVoiceName = () => {
    if (!selectedVoice) return null
    const voice = voices.find(v => v.id === selectedVoice)
    return voice?.name || selectedVoice
  }

  // Check if we have any video content generated
  const hasGeneratedVideo =
    formData.shotList &&
    formData.shotList.some(scene =>
      scene.shots.some(shot => shot.vidSrc || shot.video_duration)
    )

  return (
    <div className="voiceover-step">
      {!isDraftMode && (
        <StepTitle
          iconType="sound"
          title="Voiceover"
          description="Select a voice for your video narration."
        />
      )}

      <div className="voiceover-step__content">
        <div className="voiceover-step__mapping-card">
          <div className="voiceover-step__mapping-header">
            <div>
              <h3 className="voiceover-step__mapping-title">Audio Mapping</h3>
              <p className="voiceover-step__mapping-desc">
                Assign specific voices to the narrator and characters extracted
                from your script.
              </p>
            </div>
            {/* <AudioModeToggle mode={audioMode} onChange={handleModeChange} /> */}
          </div>

          <div className="voiceover-step__mapping-sections">
            {/* Narrator Section */}
            <div className="voiceover-step__mapping-section">
              <h4 className="voiceover-step__mapping-section-title">
                Narrator
              </h4>
              <div className="voiceover-step__mapping-table-wrapper">
                <table className="voiceover-step__mapping-table">
                  <thead>
                    <tr>
                      <th>Target</th>
                      <th>Assigned Voice</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      className={
                        activeTarget === 'narrator' ? 'row--active' : ''
                      }
                    >
                      <td>
                        <div className="target-cell">
                          <Avatar
                            size={32}
                            icon="sound"
                            className={`target-avatar ${
                              activeTarget === 'narrator'
                                ? 'target-avatar--active'
                                : ''
                            }`}
                          />
                          <span className="target-name">Narrator</span>
                        </div>
                      </td>
                      <td>
                        <div
                          className={`voice-badge ${
                            !formData?.selectedVoice ? 'voice-badge--empty' : ''
                          }`}
                        >
                          {formData?.selectedVoice ? (
                            <>
                              <Icon type="check-circle" theme="filled" />
                              <span>
                                {voices.find(
                                  v => v.id === formData.selectedVoice
                                )?.name || 'Custom Voice'}
                              </span>
                            </>
                          ) : (
                            <span>No Voice Assigned</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {formData?.selectedVoice ? (
                          <span className="status-badge status-badge--mapped">
                            <Icon type="check-circle" theme="filled" />
                            Mapped
                          </span>
                        ) : (
                          <span className="status-badge status-badge--pending">
                            Unmapped
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="action-cell">
                          {formData?.selectedVoice && (
                            <Button
                              size="small"
                              shape="circle"
                              icon={
                                playingVoice === formData.selectedVoice
                                  ? 'pause'
                                  : 'caret-right'
                              }
                              onClick={e => {
                                e.stopPropagation()
                                handlePlayPreview(formData.selectedVoice)
                              }}
                              className={`preview-btn ${
                                playingVoice === formData.selectedVoice
                                  ? 'preview-btn--playing'
                                  : ''
                              }`}
                            />
                          )}
                          <Button
                            type={
                              activeTarget === 'narrator'
                                ? 'primary'
                                : 'default'
                            }
                            size="small"
                            className="select-btn"
                            onClick={e => {
                              e.stopPropagation()
                              setActiveTarget('narrator')
                              setIsSelectionModalVisible(true)
                            }}
                          >
                            {formData?.selectedVoice
                              ? 'Change Voice'
                              : 'Select Voice'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Character Selection Section - Only show in Dialogue mode */}
            {audioMode === 'dialogue' && (
              <div className="voiceover-step__mapping-section character-section">
                <h4 className="voiceover-step__mapping-section-title">
                  Characters
                </h4>

                {isDraftMode &&
                !shotGenerationStyle?.key &&
                !shotGenerationStyle?.id &&
                (!formData?.shotList || formData.shotList.length === 0) ? (
                  <div className="character-loading-state waiting-state">
                    <div className="processing-visual">
                      <div className="style-selection-icon">
                        <Icon
                          type="edit"
                          style={{ fontSize: '32px', color: '#0047ff' }}
                        />
                      </div>
                    </div>
                    <h3>Waiting for Style Selection</h3>
                    <p>
                      Please select an Editing Style in Step 2 to identify
                      characters.
                    </p>
                  </div>
                ) : isDraftMode && !isShotsReady ? (
                  <div className="character-loading-state">
                    <div className="processing-visual">
                      <div className="custom-loader" />
                      <div className="loader-center">
                        <div className="inner-dot" />
                      </div>
                    </div>
                    <p>Analyzing story and identifying characters...</p>
                    {shotsGenerationProgress > 0 && (
                      <div className="shots-progress-container">
                        <Progress
                          percent={Math.round(shotsGenerationProgress)}
                          status="active"
                          size="small"
                        />
                      </div>
                    )}
                  </div>
                ) : !formData.characterMap ||
                  Object.keys(formData.characterMap || {}).length === 0 ? (
                  <div className="no-characters-state">
                    <p>No characters detected in this story.</p>
                  </div>
                ) : (
                  <div className="voiceover-step__mapping-table-wrapper">
                    <table className="voiceover-step__mapping-table">
                      <thead>
                        <tr>
                          <th>Target</th>
                          <th>Assigned Voice</th>
                          <th>Status</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(formData.characterMap || {}).map(
                          ([key, name]) => (
                            <tr
                              key={key}
                              className={
                                activeTarget === key ? 'row--active' : ''
                              }
                            >
                              <td>
                                <div className="target-cell">
                                  <Avatar
                                    size={32}
                                    icon="user"
                                    className={`target-avatar ${
                                      activeTarget === key
                                        ? 'target-avatar--active'
                                        : ''
                                    }`}
                                  />
                                  <span className="target-name">
                                    {typeof name === 'object'
                                      ? name.name
                                      : name}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div
                                  className={`voice-badge ${
                                    !formData.characterVoices?.[key]
                                      ? 'voice-badge--empty'
                                      : ''
                                  }`}
                                >
                                  {formData.characterVoices?.[key] ? (
                                    <>
                                      <Icon
                                        type="check-circle"
                                        theme="filled"
                                      />
                                      <span>
                                        {voices.find(
                                          v =>
                                            v.id ===
                                            formData.characterVoices[key]
                                        )?.name || 'Custom Voice'}
                                      </span>
                                    </>
                                  ) : (
                                    <span>Narrator (Default)</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                {formData.characterVoices?.[key] ? (
                                  <span className="status-badge status-badge--mapped">
                                    <Icon type="check-circle" theme="filled" />
                                    Mapped
                                  </span>
                                ) : (
                                  <span className="status-badge status-badge--default">
                                    Default
                                  </span>
                                )}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <div className="action-cell">
                                  {formData.characterVoices?.[key] && (
                                    <Button
                                      size="small"
                                      shape="circle"
                                      icon={
                                        playingVoice ===
                                        formData.characterVoices?.[key]
                                          ? 'pause'
                                          : 'caret-right'
                                      }
                                      onClick={e => {
                                        e.stopPropagation()
                                        handlePlayPreview(
                                          formData.characterVoices[key]
                                        )
                                      }}
                                      className={`preview-btn ${
                                        playingVoice ===
                                        formData.characterVoices?.[key]
                                          ? 'preview-btn--playing'
                                          : ''
                                      }`}
                                    />
                                  )}
                                  <Button
                                    type={
                                      activeTarget === key
                                        ? 'primary'
                                        : 'default'
                                    }
                                    size="small"
                                    className="select-btn"
                                    onClick={e => {
                                      e.stopPropagation()
                                      setActiveTarget(key)
                                      setIsSelectionModalVisible(true)
                                    }}
                                  >
                                    {formData.characterVoices?.[key]
                                      ? 'Change Voice'
                                      : 'Select Voice'}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {isClusteringAudio && (
          <Alert
            message="Generating Audio for all scenes..."
            description={
              <div style={{ marginTop: 8 }}>
                {typeof audioClusteringProgress === 'number' && (
                  <Progress
                    percent={Math.round(audioClusteringProgress)}
                    size="small"
                    status="active"
                  />
                )}
              </div>
            }
            type="info"
            showIcon
            icon={<Icon type="loading" />}
            style={{ marginBottom: 24 }}
          />
        )}
      </div>

      <VoiceSelectionModal
        visible={isSelectionModalVisible}
        onCancel={() => setIsSelectionModalVisible(false)}
        onSelect={handleSelectVoice}
        voices={voices}
        loadingVoices={loadingVoices}
        handlePlayPreview={handlePlayPreview}
        playingVoice={playingVoice}
        audioProgress={audioProgress}
        selectedVoiceId={
          activeTarget === 'narrator'
            ? selectedVoice
            : formData.characterVoices?.[activeTarget]
        }
        targetName={
          activeTarget === 'narrator'
            ? 'Narrator'
            : (typeof formData.characterMap?.[activeTarget] === 'object'
                ? formData.characterMap[activeTarget].name
                : formData.characterMap?.[activeTarget]) || 'Character'
        }
        handleUploadVoice={handleUploadVoice}
        uploading={uploading}
        uploadProgress={uploadProgress}
        uploadError={uploadError}
        setUploadError={setUploadError}
        beforeUpload={beforeUpload}
        fetchVoices={fetchVoices}
        pendingUploadFile={pendingUploadFile}
        pendingVoiceName={pendingVoiceName}
        setPendingVoiceName={setPendingVoiceName}
        onRetryUpload={handleRetryUpload}
        onOverwriteUpload={handleOverwriteUpload}
      />
      {/* Voice Change Confirmation Modal */}
      <Modal
        visible={showConfirmationModal}
        onCancel={handleCancelChange}
        footer={null}
        centered
        width={520}
        className="voiceover-change-modal"
      >
        <div className="voiceover-change-modal-content">
          <div className="modal-icon-wrapper">
            <Icon type="warning" className="modal-warning-icon" />
          </div>
          <h3 className="modal-title">Existing Generated Content Detected</h3>
          <p className="modal-description">
            You have already generated content for this video. Changing the
            voice to <strong>"{getPendingVoiceName()}"</strong> will affect your
            existing work.
            <br />
            <br />
            Please choose how you would like to proceed:
          </p>
          <div className="modal-options">
            <div className="modal-option modal-option--recommended">
              <div className="option-icon option-icon--clone">
                <Icon type="copy" />
              </div>
              <div className="option-content">
                <h4 className="option-title">
                  Clone as New Video
                  <span className="recommended-badge">Recommended</span>
                </h4>
                <p className="option-description">
                  Safely creates a brand new copy of this video with the new
                  voice. Your current video and all generated content will be
                  saved and remain untouched.
                </p>
              </div>
            </div>
            <div className="modal-option">
              <div className="option-icon">
                <Icon type="edit" />
              </div>
              <div className="option-content">
                <h4 className="option-title">Override Current Video</h4>
                <p className="option-description">
                  Updates this exact video with the new voice.
                  <span className="warning-text">
                    <Icon type="warning" /> Warning: Audio will be regenerated.
                    {hasGeneratedVideo &&
                      ' Videos with different audio duration will need to be regenerated.'}
                    Images will be preserved.
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <Button onClick={handleCancelChange} size="large">
              Cancel
            </Button>

            <div className="modal-actions-right">
              <Button
                type="default"
                onClick={handleOverride}
                size="large"
                icon="edit"
              >
                Override
              </Button>
              <Button
                type="primary"
                onClick={handleClone}
                size="large"
                icon="copy"
              >
                Clone
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Audio Mode Selection Modal */}
      <AudioModeSelectionModal
        visible={showModeModal}
        audioMode={audioMode}
        onModeChange={handleModeChange}
        onConfirm={handleConfirmMode}
      />

      {/* Audio Mode Change Confirmation Modal */}
      <AudioModeChangeModal
        visible={showChangeModal}
        pendingMode={pendingMode}
        currentMode={formData?.audioMode}
        targetHasAudio={targetHasAudio}
        onCancel={handleModeChangeCancel}
        onContinue={handleModeChangeContinue}
        onRegenerate={handleModeChangeRegenerate}
      />
    </div>
  )
}

export default Voiceover
